import { InboxEntry, UserCommunication } from '@this+that/models';
import log from 'loglevel';

import { DOBOX_CONTEXT_LIMITS, EXCLUDED_WORDS } from './constants';
import { isChatCommunication } from './doBoxSourceDetector';

const extractEmailContext = (communications: Partial<UserCommunication>[]) => {
  const emailComms = communications.filter(
    (comm) => !isChatCommunication(comm),
  );

  const allRecipients = emailComms.flatMap(
    (comm) => comm.recipients?.map((r) => r.email) || [],
  );

  const authorEmail = emailComms[0]?.authorEmail || '';
  const fromDomain = authorEmail ? authorEmail.split('@')[1] : '';

  const toDomains = [
    ...new Set(
      allRecipients.map((email) => email.split('@')[1]).filter(Boolean),
    ),
  ];

  return {
    authorEmail,
    recipients: [...new Set(allRecipients)],
    fromDomain,
    toDomains,
  };
};

const extractChatContext = (communications: Partial<UserCommunication>[]) => {
  const chatComms = communications.filter(isChatCommunication);

  const channels = [
    ...new Set(chatComms.map((comm) => comm.channelName).filter(Boolean)),
  ];

  const authors = [
    ...new Set(chatComms.map((comm) => comm.author).filter(Boolean)),
  ];

  const platform = chatComms[0]?.platform || '';
  const workspaceId =
    chatComms[0]?.slackWorkspaceId || chatComms[0]?.teamsWorkspaceId || '';

  return {
    platform,
    workspaceId,
    channels,
    authors,
  };
};

const extractKeywords = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/\s+/)
    .filter(
      (word) =>
        word.length > DOBOX_CONTEXT_LIMITS.MIN_KEYWORD_LENGTH &&
        !EXCLUDED_WORDS.has(word),
    )
    .filter((word, index, arr) => arr.indexOf(word) === index)
    .slice(0, DOBOX_CONTEXT_LIMITS.MAX_KEYWORDS * 2);

const truncateContext = (
  inboxEntry: InboxEntry,
  emailContext: ReturnType<typeof extractEmailContext>,
  chatContext: ReturnType<typeof extractChatContext>,
  keywords: string[],
): string => {
  const simplifiedContext = {
    inboxEntryId: inboxEntry.id,
    subject: inboxEntry.subject || '',
    description: (inboxEntry.description || '').substring(
      0,
      DOBOX_CONTEXT_LIMITS.DESCRIPTION_TRUNCATE_LENGTH,
    ),
    email: {
      authorEmail: emailContext.authorEmail,
      recipients: emailContext.recipients.slice(
        0,
        DOBOX_CONTEXT_LIMITS.MAX_RECIPIENTS,
      ),
      fromDomain: emailContext.fromDomain,
      toDomains: emailContext.toDomains.slice(
        0,
        DOBOX_CONTEXT_LIMITS.MAX_TO_DOMAINS,
      ),
    },
    chat: {
      platform: chatContext.platform,
      workspaceId: chatContext.workspaceId,
      channels: chatContext.channels,
      authors: chatContext.authors,
    },
    keywords: keywords.slice(0, DOBOX_CONTEXT_LIMITS.MAX_KEYWORDS),
  };

  return JSON.stringify(simplifiedContext, null, 2).substring(
    0,
    DOBOX_CONTEXT_LIMITS.MAX_JSON_LENGTH,
  );
};

const buildFallbackContext = (inboxEntry: InboxEntry): string =>
  JSON.stringify(
    {
      inboxEntryId: inboxEntry.id,
      subject: inboxEntry.subject || '',
      description: (inboxEntry.description || '').substring(
        0,
        DOBOX_CONTEXT_LIMITS.FALLBACK_DESCRIPTION_LENGTH,
      ),
      email: {
        authorEmail: '',
        recipients: [],
        fromDomain: '',
        toDomains: [],
      },
      chat: {
        platform: '',
        workspaceId: '',
        channels: [],
        authors: [],
      },
      keywords: [],
    },
    null,
    2,
  );

export const formatInboxEntryContext = (inboxEntry: InboxEntry): string => {
  try {
    const communications = inboxEntry.communications || [];
    const emailContext = extractEmailContext(communications);
    const chatContext = extractChatContext(communications);
    const keywords = extractKeywords(
      `${inboxEntry.subject || ''} ${inboxEntry.description || ''}`,
    );

    const context = {
      inboxEntryId: inboxEntry.id,
      subject: inboxEntry.subject || '',
      description: inboxEntry.description || '',
      email: {
        authorEmail: emailContext.authorEmail,
        recipients: emailContext.recipients,
        fromDomain: emailContext.fromDomain,
        toDomains: emailContext.toDomains,
      },
      chat: {
        platform: chatContext.platform,
        workspaceId: chatContext.workspaceId,
        channels: chatContext.channels,
        authors: chatContext.authors,
      },
      keywords,
    };

    const contextString = JSON.stringify(context, null, 2);

    if (contextString.length > DOBOX_CONTEXT_LIMITS.MAX_CONTEXT_LENGTH) {
      return truncateContext(inboxEntry, emailContext, chatContext, keywords);
    }

    return contextString;
  } catch (error) {
    log.warn('Error formatting inbox entry context, using fallback:', error);
    return buildFallbackContext(inboxEntry);
  }
};
